import React, { useRef, useState } from "react";
import DatePicker from "react-datepicker";
import Modalheader from "components/modal/components/header";
import ModalBody from "components/modal/components/body";
import ModalFooter from "components/modal/components/footer";
import Button from "components/button";
import LoadingButton from "components/button/loading";
import useModal from "components/modal/useModal";
import UseContext from "hooks/useContext";
import ApiDatabase from "server";
import { Information } from "assets/icons";
import StyledDateInput from "components/styledInput/StyledDateInput";
import { FormField } from "pages/createMission/components/SectionWrapper";

const ERROR_MESSAGE_KEYS = {
    PENNYLANE_CUSTOMER_NOT_FOUND: ["customerNotLinked", "Cette entreprise n'est pas reliée à un client Pennylane."],
    PENNYLANE_CUSTOMER_AMBIGUOUS: ["customerNotLinked", "Cette entreprise n'est pas reliée à un client Pennylane."],
    PENNYLANE_UNAVAILABLE: ["pennylaneUnavailable", "Pennylane ne répond pas, réessayez dans quelques minutes."],
    PENNYLANE_READS_DISABLED: ["pennylaneReadsDisabled", "La lecture de Pennylane est désactivée sur cet environnement."],
    ACCOUNT_STATEMENT_TOO_LARGE: ["tooLarge", "Ce compte contient trop d'écritures pour générer le relevé. Contactez Jobbiz pour l'obtenir."],
    INVALID_PERIOD: ["invalidPeriod", "La période est invalide."],
    ACCOUNT_STATEMENT_FORBIDDEN: ["forbidden", "Vous n'avez pas accès au relevé de compte."],
};
const DEFAULT_ERROR_MESSAGE_KEY = ["failed", "Le relevé n'a pas pu être généré."];

// Built from the local calendar fields: toISOString() would shift the day for any user east of UTC.
const toApiDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
};

// Same algorithm as the api's toFileNameSlug: a cross-origin response does not expose Content-Disposition.
const FILE_NAME_SLUG_MAX_LENGTH = 60;
const slugifyCompanyName = (name) =>
    (typeof name === "string" ? name : "")
        .normalize("NFKD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .replace(/œ/g, "oe")
        .replace(/æ/g, "ae")
        .replace(/[^a-z0-9]+/g, "-")
        .slice(0, FILE_NAME_SLUG_MAX_LENGTH)
        .replace(/^-+|-+$/g, "") || "entreprise";

const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const getStartOfYear = () => new Date(new Date().getFullYear(), 0, 1);

// ModalBody scrolls: an absolute calendar would be clipped at its bottom edge, under the footer.
const POPPER_PROPS = { strategy: "fixed" };

const ModalAccountStatement = () => {
    const { companyData, language } = UseContext();
    const { toggle } = useModal();
    const label = (key, defaultValue) => language.getValue(`accountStatement.${key}`, defaultValue);

    const [fromDate, setFromDate] = useState(getStartOfYear);
    const [toDate, setToDate] = useState(() => new Date());
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    // State updates are async: a ref blocks a second click landing before the re-render.
    const isRequestPending = useRef(false);

    const handleDownload = () => {
        if (isRequestPending.current) return;
        if (!fromDate || !toDate) {
            setErrorMessage(label("missingDate", "Indiquez une date de début et une date de fin."));
            return;
        }
        const from = toApiDate(fromDate);
        const to = toApiDate(toDate);
        if (from > to) {
            setErrorMessage(label("invertedPeriod", "La date de début doit précéder la date de fin."));
            return;
        }

        isRequestPending.current = true;
        setIsLoading(true);
        setErrorMessage("");
        ApiDatabase.getCompanyAccountStatement(
            { idCompany: companyData?._id, from, to },
            (blob) => {
                const companySlug = slugifyCompanyName(companyData?.name_commercial || companyData?.reason_social);
                downloadBlob(blob, `releve-de-compte-${companySlug}-${from}-${to}.pdf`);
                isRequestPending.current = false;
                setIsLoading(false);
                toggle();
            },
            (err) => {
                const errorCode = err?.response?.data?.errorCode;
                const [key, defaultValue] = ERROR_MESSAGE_KEYS[errorCode] || DEFAULT_ERROR_MESSAGE_KEY;
                setErrorMessage(label(`errors.${key}`, defaultValue));
                isRequestPending.current = false;
                setIsLoading(false);
            }
        );
    };

    return (
        <>
            <Modalheader
                hide={toggle}
                hideShow={true}
                description={label("description", "Choisissez la période du relevé de compte à télécharger.")}
            >
                {label("title", "Relevé de compte")}
            </Modalheader>
            <ModalBody>
                <div className={"flex flex-col gap-4"}>
                    <div className={"grid grid-cols-2 gap-6"}>
                        <FormField label={label("from", "Du")}>
                            <DatePicker
                                dateFormat="dd/MM/yyyy"
                                selected={fromDate}
                                onChange={(date) => setFromDate(date)}
                                locale="fr-FR"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                popperPlacement="bottom"
                                popperProps={POPPER_PROPS}
                                customInput={<StyledDateInput />}
                            />
                        </FormField>
                        <FormField label={label("to", "Au")}>
                            <DatePicker
                                dateFormat="dd/MM/yyyy"
                                selected={toDate}
                                onChange={(date) => setToDate(date)}
                                locale="fr-FR"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                popperPlacement="bottom"
                                popperProps={POPPER_PROPS}
                                customInput={<StyledDateInput />}
                            />
                        </FormField>
                    </div>
                    {errorMessage ? (
                        <p className="text-yellow-400 flex items-center gap-4" role="alert">
                            <Information wh={16} color={"#FFA43D"} />
                            {errorMessage}
                        </p>
                    ) : null}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button size={"LG"} color={"SECONDARY"} onClick={toggle}>
                    {label("cancel", "Annuler")}
                </Button>
                {isLoading ? (
                    <LoadingButton size={"LG"} color={"PRIMARY"}>
                        {label("downloading", "Téléchargement…")}
                    </LoadingButton>
                ) : (
                    <Button size={"LG"} color={"PRIMARY"} onClick={handleDownload}>
                        {label("download", "Télécharger")}
                    </Button>
                )}
            </ModalFooter>
        </>
    );
};

export default ModalAccountStatement;
